/*
 * Utilities
 *
 * Basically, these are functions you might want to use in other modules
 * or containers. They should be 'pure' or functional only and should not ever
 * effect application state.
 */

export function copyStringToClipboard(str) {
  // Create new element
  const el = document.createElement('textarea');
  // Set value (string to be copied)
  el.value = str;
  // Set non-editable to avoid focus and move outside of view
  el.setAttribute('readonly', '');
  el.style = { position: 'absolute', left: '-9999px' };
  document.body.appendChild(el);
  // Select text inside element
  el.select();
  // Copy text to clipboard
  document.execCommand('copy');
  // Remove temporary element
  document.body.removeChild(el);
}

export const copyUnassignedProperites = (previous, next) => {
  Object.assign(previous, next);
  Object.assign(next, previous);
  return next;
};

export const mergePropertiesOfArrayOfObjectsWithIdKeys = (previous, next) => next.map((obj2) => {
  const obj1 = previous.find(obj => (obj.id === obj2.id));
  if (obj1) {
    copyUnassignedProperites(obj1, obj2);
  }
  return obj2;
});

export const immediateInterval = (func, delay) => {
  func();
  return setInterval(func, delay);
};

export const findIndexById = (objects, id) => objects.findIndex(obj => (obj.id === id));

/**
 * sliceItemsIntoCollectionById
 *
 * This function is useful for updating a subsection of a collection
 * where the items are stable sorted, and have a id property. This assumes
 * that if there is no overlap, the items are sorted after (index larger)
 * than the collection.
 *
 * @param collection Array of objects with id property
 * @param items Array of objects with id property
 * @param stripStart Bool - remove objects that have a smaller index than the first match
 * @param stripEnd Bool - remove objects that have a larger index than the last match
 *
 * @returns copy of altered collection
 */
export const spliceItemsIntoCollectionById = (
  collection = [],
  items = [],
  stripStart,
  stripEnd,
) => {
  let newCollection = [];
  if (!items.length) {
    newCollection = [...collection];
  } else if (!collection.length) {
    newCollection = [...items];
  } else {
    const start = findIndexById(collection, items[0].id);
    const end = findIndexById(collection, items[items.length - 1].id);

    // if position is -1, it means the item was not in collection
    if (start === -1 && end === -1) {
      // case naturally handles strip end as there is no end
      newCollection = [...collection].concat(items);
    } else if (end === -1) {
      // case naturally handles strip end, as there is no end
      newCollection = stripStart ? [...items] : collection.slice(0, start).concat(items);
    } else if (start === -1) {
      // if there is no start, then all of the remaining collection is the 'end' to be stripped
      newCollection = stripEnd ? [...items] : [...items].concat(collection.slice(end + 1));
    } else {
      if (!stripStart) {
        newCollection = collection.slice(0, start);
      }

      newCollection = newCollection.concat(items);

      // case handles strip end by optionally adding the end collection items
      if (!stripEnd) {
        newCollection = newCollection.concat(collection.slice(end + 1));
      }
    }
  }
  return newCollection;
};

export const findObjById = (objects, id) => {
  try {
    return objects[findIndexById(objects, id)] || {};
  } catch (error) {
    return {};
  }
};

export const replaceObjByObjId = (objects = [], obj) => {
  const replacedObjects = [...objects];
  const objIndex = findIndexById(replacedObjects, obj.id);
  if (objIndex > -1) {
    replacedObjects[findIndexById(replacedObjects, obj.id)] = obj;
  } else {
    replacedObjects.push(obj);
  }
  return replacedObjects;
};

export const zeroPad = num => (
  `0${num}`.slice(-2)
);

export const formatHoursMinutesSeconds = (durationFloat) => {
  const secs = Math.round(durationFloat);
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor(secs / 60) - (hours * 60);
  const seconds = secs % 60;
  return `${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds)}`;
};

export const durationTimeString = (obj) => {
  if (!obj || !obj.duration) return '';

  return formatHoursMinutesSeconds(obj.duration);
};
